import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

const DigitalManagersList = React.lazy(() => import("./DigitalManagersList"));
const DigitalManagerDetail = React.lazy(() => import("./DigitalManagerDetail"));
const HireDigitalManagerPage = React.lazy(() => import("./HireDigitalManagerPage"));
const DigitalManagerTemplatesPage = React.lazy(() => import("./DigitalManagerTemplatesPage"));
const NotificationRulesPage = React.lazy(() => import("./NotificationRulesPage"));

/**
 * Digital Managers Module Config
 *
 * Long-lived AI "digital employees" with responsibilities, memory, schedule,
 * and a report-to chain. Each manager is provisioned with a real Client +
 * Employee record so its CRUD actions audit through the standard pipeline.
 *
 * SINGLE SOURCE OF TRUTH for permissions: backend `digital-managers/permissions.ts`.
 * Routes use `permissionLevel` ("view", "train", "admin") and resolve at runtime.
 */
const digitalManagersModuleConfig: ModuleConfig = {
    id: "digital-managers",
    name: "Digital Managers",
    description: "Hire long-lived AI managers with responsibilities, memory, and a report-to chain",
    version: "1.0.0",
    enabled: true,
    category: "automation",
    order: 95,
    icon: "🤖",
    color: getColor("primaryBlue"),

    routes: [
        {
            path: "/digital-managers",
            component: DigitalManagersList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/digital-managers/new",
            component: HireDigitalManagerPage,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/digital-managers/templates",
            component: DigitalManagerTemplatesPage,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/digital-managers/notification-rules",
            component: NotificationRulesPage,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/digital-managers/:id/edit",
            component: HireDigitalManagerPage,
            permissionLevel: "admin",
            useLayout: true,
        },
        {
            path: "/digital-managers/:id",
            component: DigitalManagerDetail,
            permissionLevel: "view",
            useLayout: true,
        },
    ],

    navigation: [
        {
            label: "Digital Managers",
            path: "/digital-managers",
            icon: "🤖",
            permissionLevel: "view",
        },
        {
            label: "Templates",
            path: "/digital-managers/templates",
            icon: "🧬",
            permissionLevel: "view",
        },
        {
            label: "Notification Rules",
            path: "/digital-managers/notification-rules",
            icon: "🔔",
            permissionLevel: "view",
        },
    ],

    // Required by the ModuleConfig interface but unused — runtime permissions
    // come from the backend `digital-managers/permissions.ts` SSOT via the
    // `modulePermissions` GraphQL query (resolved through `permissionLevel` on
    // routes/nav above). Empty arrays here match the convention in every other
    // tenant module (see emails/moduleConfig.ts for the canonical shape).
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        train: [],
        admin: [],
    },
};

export default digitalManagersModuleConfig;
